import { useEffect, useMemo, useState } from "react";
import BottyAI from "src/modules/data-protection/assets/images/ai-botty/bottyAi.png";
import { FormGroup, Input, Label, Form, Col, Row, Container, Spinner, Button } from "reactstrap";
import DataProtectionSoftwareService from "../../../../../apis/DataProtectionSoftwareService";
import { useMutation } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import { RiCheckLine, RiCloseCircleLine } from "react-icons/ri";

const useAutoResetMutation = (mutationFn, resetTime = 8000) => {
    const mutation = useMutation(mutationFn);

    useEffect(() => {
        let timer;
        if (mutation.isSuccess || mutation.isError) {
            timer = setTimeout(() => {
                mutation.reset();
            }, resetTime);
        }
        return () => clearTimeout(timer);
    }, [mutation.isSuccess, resetTime]);

    return mutation;
};

export default function GeneratedByAI(props) {
	const [providerUrl, setProviderUrl] = useState(props.defaultValues?.providerUrl || '');
	const [softwareName, setSoftwareName] = useState(props.defaultValues?.softwareName || '');
	const [inputErrors, setInputErrors] = useState([]);
	const [ownDevIsChecked, setOwnDevIsChecked] = useState(false);
    const [canCreateSoftware, setCanCreateSoftware] = useState(true);

	const handleValidateForm = () => {
		let isValid = true;
		const errors = {};

		// Check if softwareName is empty
		if (!softwareName.trim()) {
			errors.softwareName = props.t("Software name is required.");
			isValid = false;
		}

		// Check if providerUrl is a valid URL providerUrl.trim() && 
		if (!ownDevIsChecked && !isValidURL(providerUrl)) {
			errors.providerUrl = props.t("Valid URL is required for provider.");
			isValid = false;
		}

		setInputErrors(errors);
		return isValid;
	};

	const addSoftwareMutation = useMutation({
		mutationFn: async (software) => {
			props.changeCurrentState("loading")
			const service = DataProtectionSoftwareService.getInstance();
			return await service.generateByAI(software);
		},
		onSuccess: (data) => {
			props.generate({ 
				softwareName, 
				providerUrl, 
				isOwnDevelopment: ownDevIsChecked, 
				...data 
			})
			props.changeCurrentState("manuallyEditted");
		},
		onError: () => {
			props.changeCurrentState("failed");
		},
	});

    const checkSoftwareExistenceMutation = useAutoResetMutation({
        mutationFn: async (payload) => {
            const service = DataProtectionSoftwareService.getInstance();

            return await service.checkSoftwareExistence(payload);
        }
    });

    const debouncedHandleNameChange = useMemo(
        () =>
            debounce((newName) => {
                setCanCreateSoftware(false);

                checkSoftwareExistenceMutation.mutate(
                    { service: newName },
                    {
                        onSuccess: ({ exists }) => {
                            setCanCreateSoftware(!exists);
                        },
                    }
                );
            }, 900),
        []
    );

    useEffect(() => {
        return () => {
            debouncedHandleNameChange.cancel();
        };
    }, [debouncedHandleNameChange]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (handleValidateForm()) {
			if(!ownDevIsChecked){
				addSoftwareMutation.mutate({
					software_name: softwareName,
                    url: providerUrl,
                    organization_id: props.refOrgId
				});
			}
			else{
				props.generate({ softwareName, isOwnDevelopment: true });
				props.changeCurrentState("manuallyEditted");
			}
		}
	};

	// Function to check if a URL is valid
	const isValidURL = (url) => {
		const expression = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;
		return expression.test(url);
	};

	return (
		<div style={{ textAlign: "center" }}>
			<img
				className="my-3"
				src={BottyAI}
				width={"50%"}
				height={"50%"}
				alt="Botty"
				style={{ marginInline: "auto" }}
			/>
			<Form onSubmit={handleSubmit}>
				<Container>
					<Row className="justify-content-center mb-4">
						<Col sm='12' md='8'>
							<h4
								className="mt-2 mb-4 text-dark"
							>
								{props.t("Adding new asset? Let me help you with that!")}
							</h4>
							<div className="d-flex flex-column align-items-start">
								<FormGroup>
									<Input
										type="checkbox"
										className="me-1"
										id="own-dev-use-check"
										onChange={(e) => setOwnDevIsChecked(e.target.checked)}
										value={ownDevIsChecked}
									/>
									<Label htmlFor="own-dev-use-check">
										{props.t("Own Development/Internal Use")}
									</Label>
								</FormGroup>

								<FormGroup 
									className="w-100 mb-2"
									style={{
										textAlign: 'left'
									}}
								>
									<Label
										className={inputErrors.softwareName ? "text-danger" : "text-dark"}
									>
										{props.t("Name of Software:")}
									</Label>
									<Input
										type="text"
										value={softwareName}
										onChange={(e) => {
											setSoftwareName(e.target.value);
                                            debouncedHandleNameChange(e.target.value);
											setInputErrors((prevErrors) => ({
												...prevErrors,
												softwareName: "",
											}));
										}}
										invalid={!!inputErrors.softwareName}
									/>

                                    {checkSoftwareExistenceMutation.isLoading && (
                                        <>
                                            <Spinner size="sm" color="info" />
                                            <small className="text-info ms-1">
                                                {props.t("Checking software name")}...
                                            </small>
                                        </>
                                    )}

                                    {checkSoftwareExistenceMutation.isSuccess && checkSoftwareExistenceMutation.data.exists && (
                                        <small className="text-danger">
                                            <RiCloseCircleLine size="16" className="me-1" />
                                            {props.t("This software name is already in use.")}
                                        </small>
                                    )}

                                    {checkSoftwareExistenceMutation.isSuccess && !checkSoftwareExistenceMutation.data.exists && (
                                        <small className="text-success">
                                            <RiCheckLine size="16" className="me-1" />
                                            {props.t("This software name is available.")}
                                        </small>
                                    )}

                                    {checkSoftwareExistenceMutation.isError && (
                                        <small className="text-danger">
                                            <RiCloseCircleLine size="16" className="me-1" />
                                            {props.t("Unable to verify the software name. Please try again.")}
                                        </small>
                                    )}

									{inputErrors.softwareName && (
										<small className="text-danger">
											{inputErrors.softwareName}
										</small>
									)}
								</FormGroup>

								{!ownDevIsChecked && (
									<FormGroup
										className="w-100 mb-2"
										style={{
											textAlign: 'left'
										}}
									>
										<Label
											className={inputErrors.providerUrl ? "text-danger" : "text-dark"}
										>
											{props.t("Page of The Software Provider (URL):")}
										</Label>
										<Input
											type="text"
											value={providerUrl}
											onChange={(e) => {
												setProviderUrl(e.target.value);
												setInputErrors((prevErrors) => ({
													...prevErrors,
													providerUrl: "",
												}));
											}}
											invalid={!!inputErrors.providerUrl}
										/>
										{inputErrors.providerUrl && (
											<small className="text-danger">{inputErrors.providerUrl}</small>
										)}
									</FormGroup>
								)}
							</div>
						</Col>
					</Row>
					<Row className="justify-content-center">
						<Col sm='12' md='8'>
							<div className="d-flex justify-content-end gap-2">
								<Button
									color="secondary"
									type="button"
									onClick={() => props.toggleModal()}
								>
									{props.t("Cancel")}
								</Button>
								<Button 
									color="primary"
									type="submit"
									disabled={!canCreateSoftware}
								>
									{props.t("Done")}
								</Button>
			
							</div>
						</Col>
					</Row>
				</Container>
			</Form>
		</div>
	);
}
